import { Text } from "@next-platform/client-common-spicy-ui";
import { memo, useCallback } from "react";

import { getCurrencySymbol } from "@/utils";

import { BillingCycleId } from "../utils/buildDurationPlans";

const PER_DAY_SUFFIX = "/day";
const ARROW_GLYPH = "→";
const CHOOSE_PREFIX = "Choose ";
const SAVE_PREFIX = "Save ";
const SAVE_SUFFIX = "%";

const LABEL_BY_CYCLE: Record<BillingCycleId, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annual",
};

const BILLED_BY_CYCLE: Record<BillingCycleId, string> = {
  monthly: "Billed monthly",
  quarterly: "Billed every 3 months",
  annually: "Billed once a year",
};

type DurationCardProps = {
  readonly id: BillingCycleId;
  readonly planId: string;
  readonly total: number;
  readonly perDay: number;
  readonly save?: number;
  readonly currency: string;
  readonly onSelect: (planId: string, price: number, currency: string) => void;
};

function DurationCard({ id, planId, total, perDay, save, currency, onSelect }: DurationCardProps) {
  const currencySymbol = getCurrencySymbol(currency);
  const labelText = LABEL_BY_CYCLE[id];
  const billedText = BILLED_BY_CYCLE[id];
  const chooseText = `${CHOOSE_PREFIX}${labelText}`;
  const handleClick = useCallback(() => onSelect(planId, total, currency), [onSelect, planId, total, currency]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group flex w-full cursor-pointer items-center gap-3 rounded-[18px] border-[1.5px] border-gray-200 bg-white p-[18px] text-left shadow-sm transition active:scale-[0.985] hover:border-blue-500/60 hover:bg-gray-50 dark:border-white/10 dark:bg-[#141418] dark:shadow-none dark:hover:bg-[#181820] md:flex-col md:items-stretch md:gap-3.5 md:p-7 md:text-center md:hover:-translate-y-[3px]"
      data-testid={`DurationCard-${id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-white/70 md:justify-center">
          {labelText}
          {save ? (
            <span className="rounded-full bg-emerald-500/15 px-[7px] py-[3px] text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400">
              {SAVE_PREFIX}
              {save}
              {SAVE_SUFFIX}
            </span>
          ) : null}
        </div>
        <div className="mb-1.5 flex items-baseline text-gray-900 dark:text-white md:justify-center">
          <Text className="leading-[0.95] tracking-tight md:text-[52px]">
            {currencySymbol}
            {perDay.toFixed(2)}
          </Text>
          <span className="ml-1.5 text-[15px] font-semibold text-gray-600 dark:text-white/50">{PER_DAY_SUFFIX}</span>
        </div>
        <div className="text-[12.5px] text-gray-600 dark:text-white/45">
          {currencySymbol}
          {total.toFixed(2)} · {billedText}
        </div>
      </div>
      <span
        className="inline-flex h-10 min-w-10 shrink-0 items-center justify-center gap-2 rounded-full bg-blue-500 px-3.5 text-[15px] font-bold text-white shadow-[0_6px_18px_hsl(220_95%_60%/0.45)] transition group-hover:translate-x-0.5 md:h-[50px] md:w-full md:rounded-xl md:group-hover:translate-x-0 md:group-hover:brightness-110"
        data-testid={`DurationCard-${id}-Button`}
      >
        <span className="hidden md:inline">{chooseText}</span>
        <span aria-hidden="true" className="md:hidden">
          {ARROW_GLYPH}
        </span>
      </span>
    </button>
  );
}

export default memo(DurationCard);
